/* ==========================================================================
   BRYVO: forms.js
   Behaviour for the three Shopify contact forms (general contact, guided project
   enquiry, mini-audit request):
     - inline validation with clear messages
     - the five-step enquiry: progress, back/next, conditional questions, review
     - answers carried over from the Website Planner
     - drafts kept in this browser tab only (sessionStorage), so nothing is lost
       if Shopify sends the visitor back with an error
     - double-submission guard
   Submission itself is a normal form POST to Shopify. The confirmation panel is
   rendered by Shopify (form.posted_successfully?), never faked here.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__bryvoForms) return;
  window.__bryvoForms = true;

  var doc = document;
  var DRAFT_PREFIX = 'bryvo:draft:';
  var PLANNER_KEY = 'bryvo:planner';
  var reduce = doc.documentElement.getAttribute('data-motion') === 'off';
  var EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var WEB = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}([/?#].*)?$/i;

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  /* ---------- Tab-only storage (never sent anywhere) ---------- */
  function store() {
    try {
      var s = window.sessionStorage;
      s.getItem(PLANNER_KEY);
      return s;
    } catch (error) {
      return null;
    }
  }

  function readJSON(key) {
    var s = store();
    if (!s) return null;
    try {
      return JSON.parse(s.getItem(key) || 'null');
    } catch (error) {
      return null;
    }
  }

  function writeJSON(key, value) {
    var s = store();
    if (!s) return;
    try {
      s.setItem(key, JSON.stringify(value));
    } catch (error) {
      /* storage full or blocked: the form still works without drafts */
    }
  }

  function removeKey(key) {
    var s = store();
    if (!s) return;
    try {
      s.removeItem(key);
    } catch (error) {
      /* nothing to do */
    }
  }

  /* ---------- Field helpers ---------- */
  function fieldName(el) {
    return el.name || el.getAttribute('data-name') || '';
  }

  function isDataControl(el) {
    return !!fieldName(el) && el.type !== 'hidden' && el.type !== 'submit' && el.type !== 'button' && !el.hasAttribute('data-trap');
  }

  function serialize(form) {
    var data = {};
    each(form.elements, function (el) {
      if (!isDataControl(el)) return;
      var name = fieldName(el);
      if (el.type === 'radio') {
        if (el.checked) data[name] = el.value;
      } else if (el.type === 'checkbox') {
        data[name] = el.checked;
      } else {
        data[name] = el.value;
      }
    });
    return data;
  }

  function restore(form, data) {
    each(form.elements, function (el) {
      if (!isDataControl(el)) return;
      var name = fieldName(el);
      if (!Object.prototype.hasOwnProperty.call(data, name)) return;
      if (el.type === 'radio') el.checked = el.value === data[name];
      else if (el.type === 'checkbox') el.checked = !!data[name];
      else el.value = data[name];
    });
  }

  function errorNode(el) {
    var field = el.closest('[data-field]');
    return field ? field.querySelector('.field__error') : null;
  }

  function setError(el, message) {
    var node = errorNode(el);
    el.setAttribute('aria-invalid', 'true');
    if (node) {
      node.textContent = message;
      node.hidden = false;
    }
  }

  function clearError(el) {
    if (!el || !el.removeAttribute) return;
    var node = errorNode(el);
    el.removeAttribute('aria-invalid');
    if (node) {
      node.textContent = '';
      node.hidden = true;
    }
  }

  function controlMessage(el, strings) {
    if (el.type === 'radio' || el.type === 'checkbox') return '';
    var value = (el.value || '').trim();
    if (el.required && !value) return strings.required;
    if (!value) return '';
    if (el.type === 'email' && !EMAIL.test(value)) return strings.email;
    if (el.getAttribute('data-validate') === 'url' && !WEB.test(value)) return strings.url;
    return '';
  }

  /* Validates every enabled control inside `scope`. Returns the first invalid element, or null. */
  function validateScope(scope, form, strings) {
    var firstInvalid = null;
    each(scope.querySelectorAll('input, select, textarea'), function (el) {
      if (el.disabled || !isDataControl(el)) return;
      var message = controlMessage(el, strings);
      if (message) {
        setError(el, message);
        if (!firstInvalid) firstInvalid = el;
      } else {
        clearError(el);
      }
    });

    var groups = [];
    if (scope.hasAttribute && scope.hasAttribute('data-required-group')) groups.push(scope);
    each(scope.querySelectorAll('[data-required-group]'), function (group) {
      groups.push(group);
    });
    groups.forEach(function (group) {
      var name = group.getAttribute('data-required-group');
      var radios = group.querySelectorAll('input[type="radio"]');
      var chosen = false;
      each(radios, function (radio) {
        if (radio.name === name && radio.checked) chosen = true;
      });
      var note = group.querySelector('[data-group-error]');
      if (note) {
        note.textContent = chosen ? '' : strings.choose;
        note.hidden = chosen;
      }
      if (!chosen && !firstInvalid && radios.length) firstInvalid = radios[0];
    });
    return firstInvalid;
  }

  function focusElement(el) {
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
    } catch (error) {
      el.focus();
    }
    var rect = el.getBoundingClientRect();
    if (rect.top < 110 || rect.bottom > window.innerHeight - 40) {
      window.scrollTo({ top: Math.max(0, window.pageYOffset + rect.top - 160), behavior: reduce ? 'auto' : 'smooth' });
    }
  }

  /* Browsers reset focus when they jump to the #form anchor after a submission,
     so wait until the page has settled before moving focus to a result panel. */
  function whenSettled(fn) {
    if (doc.readyState === 'complete') window.setTimeout(fn, 80);
    else window.addEventListener('load', function () { window.setTimeout(fn, 80); }, { once: true });
  }

  function parseStrings(wrap) {
    var defaults = {
      required: 'Please fill in this field.',
      email: 'Enter a valid email address.',
      url: 'Enter a web address.',
      choose: 'Please choose an option.',
      blocked: 'That could not be sent. Please refresh the page and try again.',
      sending: 'Sending…',
      stepCount: 'Step [current] of [total]: [title]',
      edit: 'Edit',
      plannerSuggestion: 'Planner suggestion',
      features: 'Features needed'
    };
    var node = wrap.querySelector('[data-form-strings]');
    if (!node) return defaults;
    try {
      var parsed = JSON.parse(node.textContent);
      Object.keys(parsed).forEach(function (key) {
        if (parsed[key]) defaults[key] = parsed[key];
      });
    } catch (error) {
      /* keep the built-in wording */
    }
    return defaults;
  }

  function setHidden(form, name, value) {
    var input = null;
    each(form.querySelectorAll('input[type="hidden"]'), function (el) {
      if (el.name === name) input = el;
    });
    if (!value) {
      if (input) input.parentNode.removeChild(input);
      return;
    }
    if (!input) {
      input = doc.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  /* ---------- Checkbox groups: sent as one tidy line instead of one line per box ---------- */
  function prepareCheckGroups(form) {
    each(form.querySelectorAll('[data-check-group] input[type="checkbox"]'), function (box) {
      if (box.name) {
        box.setAttribute('data-name', box.name);
        box.removeAttribute('name');
      }
    });
  }

  function checkedLabels(group) {
    var labels = [];
    each(group.querySelectorAll('input[type="checkbox"]'), function (box) {
      if (box.checked && !box.disabled) labels.push(box.getAttribute('data-check-label') || box.value);
    });
    return labels;
  }

  function compileCheckGroups(form) {
    each(form.querySelectorAll('[data-check-group]'), function (group) {
      setHidden(form, group.getAttribute('data-check-group'), checkedLabels(group).join(', '));
    });
  }

  /* ---------- Conditional questions (project enquiry) ---------- */
  function applyProjectType(form) {
    var chosen = form.querySelector('input[data-type-key]:checked');
    var key = chosen ? chosen.getAttribute('data-type-key') : '';
    each(form.querySelectorAll('[data-show-for]'), function (block) {
      var show = !!key && (' ' + block.getAttribute('data-show-for') + ' ').indexOf(' ' + key + ' ') > -1;
      block.hidden = !show;
      each(block.querySelectorAll('input, select, textarea'), function (el) {
        el.disabled = !show;
      });
    });
  }

  /* ---------- Website Planner hand-over ---------- */
  function importPlanner(form, strings) {
    var box = form.querySelector('[data-planner-carry]');
    var data = readJSON(PLANNER_KEY);
    if (!box || !data || !data.result || !data.result.title) return;

    var suggestion = data.result.title + (data.result.price ? ' (' + data.result.price + ')' : '');
    var answers = (data.answers || []).map(function (item) {
      return item.q + ': ' + item.a;
    });
    var resultNode = box.querySelector('[data-planner-carry-result]');
    var answersNode = box.querySelector('[data-planner-carry-answers]');
    if (resultNode) resultNode.textContent = suggestion;
    if (answersNode) answersNode.textContent = answers.join(' · ');
    setHidden(form, 'contact[Planner suggestion]', suggestion);
    setHidden(form, 'contact[Planner answers]', answers.join('; '));
    box.hidden = false;

    if (!form.querySelector('input[data-type-key]:checked') && data.need) {
      var radio = form.querySelector('input[data-type-key="' + data.need + '"]');
      if (radio) radio.checked = true;
    }

    var removeButton = box.querySelector('[data-planner-carry-remove]');
    if (removeButton) {
      removeButton.addEventListener('click', function () {
        box.hidden = true;
        setHidden(form, 'contact[Planner suggestion]', '');
        setHidden(form, 'contact[Planner answers]', '');
        removeKey(PLANNER_KEY);
        var heading = form.querySelector('[data-step]:not([hidden]) [data-step-heading]');
        if (heading) heading.focus({ preventScroll: true });
      });
    }
  }

  /* ---------- Step-by-step mode ---------- */
  function createSteps(wrap, form, strings) {
    var steps = Array.prototype.slice.call(form.querySelectorAll('[data-step]'));
    var head = form.querySelector('[data-steps-head]');
    var count = form.querySelector('[data-step-count]');
    var bar = form.querySelector('[data-step-bar]');
    var progress = form.querySelector('[data-step-progress]');
    var dots = form.querySelectorAll('[data-step-dot]');
    var back = form.querySelector('[data-step-back]');
    var next = form.querySelector('[data-step-next]');
    var submit = form.querySelector('[data-submit]');
    var current = 0;
    if (!steps.length || !back || !next || !submit) return null;

    wrap.classList.add('is-stepped');
    if (head) head.hidden = false;

    function labelFor(el) {
      var label = el.id ? form.querySelector('label[for="' + el.id + '"] span') : null;
      return label ? label.textContent.trim() : fieldName(el);
    }

    function addReviewRow(list, label, value, stepIndex) {
      var row = doc.createElement('div');
      var dt = doc.createElement('dt');
      var dd = doc.createElement('dd');
      var text = doc.createElement('span');
      var edit = doc.createElement('button');
      var hiddenLabel = doc.createElement('span');
      row.className = 'review__row';
      dt.textContent = label;
      text.className = 'review__value';
      text.textContent = value;
      edit.type = 'button';
      edit.className = 'text-button';
      edit.setAttribute('data-goto', String(stepIndex));
      edit.textContent = strings.edit;
      hiddenLabel.className = 'visually-hidden';
      hiddenLabel.textContent = ': ' + label;
      edit.appendChild(hiddenLabel);
      dd.appendChild(text);
      dd.appendChild(edit);
      row.appendChild(dt);
      row.appendChild(dd);
      list.appendChild(row);
    }

    function buildReview() {
      var box = form.querySelector('[data-review]');
      var list = form.querySelector('[data-review-list]');
      if (!box || !list) return;
      list.textContent = '';
      steps.forEach(function (step, index) {
        if (step.hasAttribute('data-step-review')) return;
        each(step.querySelectorAll('input, select, textarea'), function (el) {
          if (el.disabled || !isDataControl(el) || el.type === 'checkbox') return;
          if (el.type === 'radio') {
            if (el.checked) addReviewRow(list, step.getAttribute('data-step-title') || fieldName(el), el.value, index);
            return;
          }
          var value = (el.value || '').trim();
          if (value) addReviewRow(list, labelFor(el), value, index);
        });
        each(step.querySelectorAll('[data-check-group]'), function (group) {
          var holder = group.closest('[data-show-for]');
          if (holder && holder.hidden) return;
          var labels = checkedLabels(group);
          if (labels.length) addReviewRow(list, strings.features, labels.join(', '), index);
        });
      });
      box.hidden = !list.children.length;
    }

    function go(index, moveFocus, direction) {
      current = Math.max(0, Math.min(steps.length - 1, index));
      steps.forEach(function (step, i) {
        step.hidden = i !== current;
        step.classList.remove('is-entering');
      });
      var step = steps[current];
      if (!reduce) {
        step.style.setProperty('--dir', direction < 0 ? '-1.5rem' : '1.5rem');
        void step.offsetWidth;
        step.classList.add('is-entering');
      }
      var text = strings.stepCount
        .replace('[current]', String(current + 1))
        .replace('[total]', String(steps.length))
        .replace('[title]', step.getAttribute('data-step-title') || '');
      if (count) count.textContent = text;
      if (bar) bar.style.setProperty('--progress', ((current + 1) / steps.length).toFixed(3));
      if (progress) {
        progress.setAttribute('aria-valuenow', String(current + 1));
        progress.setAttribute('aria-valuetext', text);
      }
      each(dots, function (dot, i) {
        dot.classList.toggle('is-current', i === current);
        dot.classList.toggle('is-done', i < current);
      });
      back.hidden = current === 0;
      next.hidden = current === steps.length - 1;
      submit.hidden = current !== steps.length - 1;
      if (step.hasAttribute('data-step-review')) buildReview();
      if (moveFocus) {
        focusElement(step.querySelector('[data-step-heading]') || step);
        var top = wrap.getBoundingClientRect().top;
        if (top < 90 || top > window.innerHeight * 0.55) {
          window.scrollTo({ top: Math.max(0, window.pageYOffset + top - 110), behavior: reduce ? 'auto' : 'smooth' });
        }
      }
    }

    next.addEventListener('click', function () {
      var invalid = validateScope(steps[current], form, strings);
      if (invalid) {
        focusElement(invalid);
        return;
      }
      go(current + 1, true, 1);
    });

    back.addEventListener('click', function () {
      go(current - 1, true, -1);
    });

    form.addEventListener('click', function (event) {
      var edit = event.target.closest ? event.target.closest('[data-goto]') : null;
      if (edit) go(parseInt(edit.getAttribute('data-goto'), 10) || 0, true, -1);
    });

    /* Enter moves to the next step instead of sending a half-finished form. */
    form.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' || current === steps.length - 1) return;
      var el = event.target;
      if (el.tagName === 'INPUT' || el.tagName === 'SELECT') {
        event.preventDefault();
        next.click();
      }
    });

    go(0, false, 1);

    return {
      go: go,
      validateAll: function () {
        for (var i = 0; i < steps.length; i++) {
          var invalid = validateScope(steps[i], form, strings);
          if (invalid) {
            go(i, false, -1);
            return invalid;
          }
        }
        return null;
      }
    };
  }

  /* ---------- One form ---------- */
  function initForm(wrap) {
    if (wrap.getAttribute('data-ready')) return;
    wrap.setAttribute('data-ready', 'true');

    var kind = wrap.getAttribute('data-form-kind') || 'form';
    var draftKey = DRAFT_PREFIX + kind;

    /* Shopify has confirmed receipt: clear the saved draft and move focus to the confirmation. */
    var success = wrap.querySelector('[data-form-success]');
    if (success) {
      removeKey(draftKey);
      if (kind === 'enquiry') removeKey(PLANNER_KEY);
      whenSettled(function () { focusElement(success); });
      return;
    }

    var form = wrap.querySelector('form');
    if (!form) return;
    var strings = parseStrings(wrap);
    var submit = form.querySelector('[data-submit]');
    var submitLabel = form.querySelector('[data-submit-label]');
    var idleLabel = submitLabel ? submitLabel.textContent : '';
    var saveTimer = 0;

    form.noValidate = true;
    prepareCheckGroups(form);

    var draft = readJSON(draftKey);
    if (draft) restore(form, draft);

    if (kind === 'enquiry') importPlanner(form, strings);
    applyProjectType(form);

    var steps = wrap.hasAttribute('data-multistep') ? createSteps(wrap, form, strings) : null;

    var serverError = wrap.querySelector('[data-form-errors]');
    if (serverError) whenSettled(function () { focusElement(serverError); });

    function save() {
      writeJSON(draftKey, serialize(form));
    }

    function queueSave() {
      window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(save, 350);
    }

    function resetSubmitting() {
      form.removeAttribute('data-submitting');
      if (submit) submit.removeAttribute('aria-disabled');
      if (submitLabel) submitLabel.textContent = idleLabel;
    }

    form.addEventListener('input', function (event) {
      if (event.target.getAttribute('aria-invalid')) clearError(event.target);
      queueSave();
    });

    form.addEventListener('change', function (event) {
      var el = event.target;
      if (el.hasAttribute('data-type-key')) applyProjectType(form);
      if (el.type === 'radio') {
        var group = el.closest('[data-required-group]');
        var note = group ? group.querySelector('[data-group-error]') : null;
        if (note) {
          note.textContent = '';
          note.hidden = true;
        }
      }
      queueSave();
    });

    /* Friendly web-address handling: add https:// when a bare domain is typed. */
    form.addEventListener('focusout', function (event) {
      var el = event.target;
      if (!el.getAttribute || el.getAttribute('data-validate') !== 'url') return;
      var value = (el.value || '').trim();
      if (value && WEB.test(value) && !/^https?:\/\//i.test(value)) el.value = 'https://' + value;
      else el.value = value;
    });

    form.addEventListener('submit', function (event) {
      if (form.getAttribute('data-submitting')) {
        event.preventDefault();
        return;
      }
      var invalid = steps ? steps.validateAll() : validateScope(form, form, strings);
      if (invalid) {
        event.preventDefault();
        focusElement(invalid);
        return;
      }
      var trap = form.querySelector('[data-trap]');
      if (trap && trap.value) {
        event.preventDefault();
        if (submitLabel) submitLabel.textContent = strings.blocked;
        return;
      }
      compileCheckGroups(form);
      window.clearTimeout(saveTimer);
      save();
      form.setAttribute('data-submitting', 'true');
      if (submit) submit.setAttribute('aria-disabled', 'true');
      if (submitLabel) submitLabel.textContent = strings.sending;
    });

    /* Returning with the browser's Back button: make the form usable again. */
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) resetSubmitting();
    });
  }

  function initAll(scope) {
    each((scope || doc).querySelectorAll('[data-form-wrap]'), function (wrap) {
      try {
        initForm(wrap);
      } catch (error) {
        /* If enhancement fails the plain HTML form still submits to Shopify. */
        if (window.console && console.warn) console.warn('[BRYVO] form enhancement skipped: ' + (error && error.message));
      }
    });
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { initAll(doc); });
  else initAll(doc);

  doc.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
